<template>
  <div>
    <div ref="emptyPreviewsContainer" class="d-none"></div>
    <div ref="emptyDropzoneContainer" class="d-none"></div>

    <div class="floating-uploader" v-if="files.length > 0" :class="{'collapsed': collapsed}">
      <div class="floating-uploader-header d-flex p-2"
           @click="collapsed = !collapsed">
        <div class="flex-grow-1 d-flex align-items-center justify-content-between mr-2">
          <div v-if="uploadingCount > 0 && files.length > 0">
            {{ queueCompleted ? 'UPLOADED' : 'UPLOADING' }} {{ uploadingCount }} of {{ files.length }} ASSETS
          </div>
          <div v-else>
            UPLOADING {{ files.length }} ASSETS
          </div>
          <div v-if="filesUploading.length > 0"
               class="text-primary cursor-pointer"
               @click.prevent.stop="handleCancelAll">Cancel All
          </div>
        </div>
        <div>
          <i class="fas text-success cursor-pointer"
             :class="{
                            'fa-spin': uploading,
                            'fa-check': uploadedAllFilesSuccessfully,
                            'fa-sync-alt': !uploadedAllFilesSuccessfully,
                            'text-danger': (!uploading && filesWithErrors.length > 0)
                        }"
             v-b-tooltip.hover
             :title="(!uploading && filesWithErrors.length > 0) ? 'Retry All Failed' : null"
             @click.prevent.stop="retryAllFailed"/>
          <i class="fas ml-2"
             :class="{'fa-angle-up': collapsed, 'fa-angle-down': !collapsed}"/>
        </div>
      </div>
      <div class="floating-uploader-items" :class="{'d-none' : collapsed}">
        <upload-item v-for="file in files"
                     :file="file"
                     :submission="submission"
                     @remove="removeFile(file)"
                     @retry="retryFile(file)"/>
      </div>
    </div>

    <upload-form ref="uploadForm"
                 :tags-by-category="tagsByCategory"
                 :categories="categories"
                 :release-forms="releaseForms"
                 :release-forms-needed="releaseFormsNeeded"
                 :release-form-url="releaseFormUrl"
                 @skip="handleFormSkip"
                 @submit="handleFormSubmit"/>

    <b-toast variant="danger" solid title="Error" v-model="maxFilesToastVisible">
      You have reached the limit of files you can upload in this submission
    </b-toast>
  </div>
</template>

<script>
import Dropzone from 'dropzone'
import normalize from "json-api-normalize";
import fileMethods from '../../../mixins/FileMixins'
import generalMixins from '../../../mixins/GeneralMixins'
import PreventNavigationMixins from "../../../mixins/PreventNavigationMixins";
import FileObject from '../../../models/FileObject'
import Attributes from "../../../models/Attributes";
import UploadItem from './SubmissionUploadItem'
import UploadForm from './SubmissionUploadForm'

export default {
  props: [
    'url',
    'paramName',
    'dropArea',
    'assetType',
    'tagsByCategory',
    'categories',
    'submission',
    'releaseForms',
    'releaseFormsNeeded',
    'releaseFormUrl',
    'maxFiles',
    'maxFileSize',
    'filesCount'
  ],

  data: function () {
    return {
      files: [],
      collapsed: true,
      formAssetParams: {},
      queueCompleted: false,
      maxFilesToastVisible: false,
    }
  },

  watch: {
    canAddAssets: function (val) {
      this.$emit('canAddAssets', val);
    }
  },

  computed: {
    acceptedFiles() {
      if (this.assetType === "video") {
        return "video/mp4,video/quicktime"
      } else if (this.assetType === "photo") {
        return "image/jpeg"
      } else if (this.assetType === "illustration") {
        return "application/postscript,application/eps,application/x-eps,image/eps,image/x-eps,application/pdf,image/svg+xml,image/png,image/jpeg"
      }

      return null
    },

    canAddAssets: function () {
      if (this.maxFiles > 0) {
        return this.filesCount + this.files.length < this.maxFiles;
      }
      return true;
    },

    filesUploading: function () {
      return this.files.filter(fileObject => fileObject.status === FileObject.UPLOADING)
    },

    filesWithErrors: function () {
      return this.files.filter(fileObject => fileObject.status === FileObject.ERROR)
    },

    filesWithSuccess: function () {
      return this.files.filter(fileObject => fileObject.status === FileObject.SUCCESS)
    },

    uploadedAllFilesSuccessfully: function () {
      return this.filesWithSuccess.length === this.files.length && this.files.length > 0
    },

    uploading: function () {
      return this.filesUploading.length > 0;
    },

    uploadingCount: function () {
      return this.filesWithSuccess.length + this.filesUploading.length;
    }
  },

  mixins: [fileMethods, generalMixins, PreventNavigationMixins],

  methods: {
    openFilePicker: function () {
      this.dropzone.hiddenFileInput.click();
    },

    retryAllFailed: function () {
      if (this.uploadedAllFilesSuccessfully) return;

      this.filesWithErrors.filter(f => f.canRetry).forEach(fileObject => this.resetFileObject(fileObject))
      this.dropzone.processQueue();
    },

    retryFile: function (fileObject) {
      this.resetFileObject(fileObject);
      this.dropzone.processQueue();
    },

    resetFileObject: function (fileObject) {
      fileObject._file.status = Dropzone.QUEUED;
      fileObject.error = null;
      fileObject.progress = 0;
      fileObject.status = null;
    },

    removeFile: function (fileObject) {
      console.log("removing file object");
      console.log(fileObject);

      if (fileObject.presignData && fileObject.presignData.id) {
        this.$emit("removeFileId", fileObject.presignData.id)
      }

      this.dropzone.removeFile(fileObject._file)
    },

    handleFormSkip: function () {
      this.formAssetParams = {submission_id: this.submission.id};
      this.dropzone.processQueue()
    },

    handleFormSubmit: function (assetFormData) {
      this.formAssetParams = {
        ...assetFormData,
        submission_id: this.submission.id,
        usage: this.submission.asset_usage_type,
        premium: this.submission.premium
      };

      // get all files, get presigned urls for each

      const vm = this
      const fileCount = this.files.length
      let processedCount = 0

      this.files.forEach(file => {
        vm.preCreatePost(file, (error) => {
          processedCount += 1

          if (error) {
            console.log(error);
            // remove file?
            // vm.removeFile(file)
          }

          if (processedCount === fileCount) {
            vm.dropzone.processQueue()
          }
        })
      })
    },

    handleCancelAll: function () {
      this.filesUploading.forEach(file => this.removeFile(file))
    },

    reset: function () {
      this.formAssetParams = {};
    },

    isDuplicateFile: function (file) {
      const result = this.files.find(fileObject => {
        return fileObject._file.name === file.name && fileObject._file.size === file.size
      });

      return result !== null && result !== undefined;
    },

    // Called from dropzone, done is a callback for when finished
    preCreatePostForSignedURL(file, done) {
      if (file.presignData) {
        done()
        return
      }

      this.$http.post(this.url, this.formAssetParams).then(fulfilled => {
        console.log(`got presigned data: ${fulfilled.body}`);
        file.presignData = fulfilled.body
        // done()
      }, rejected => {
        // done(this.getErrorMessages(rejected.body.errors)[0])
      })
    },

    // Called from handleSubmit, done is a callback for when finished
    preCreatePost(file, done) {
      if (file.presignData) {
        done()
        return
      }

      this.$http.post(this.url, {
        asset: {
          ...this.formAssetParams
        },
        file_name: file.name
      }).then(fulfilled => {
        console.log(`got presigned data`);
        console.log(fulfilled.body);
        if (file && file._file) {
          file._file.presignData = fulfilled.body
          file.presignData = fulfilled.body
        } else {
          // This shouldn't happen though
          file.presignData = fulfilled.body
        }

        console.log(file);
        done()
      }, rejected => {
        done(this.getErrorMessages(rejected.body.errors)[0])
      })
    },

    getFileUrl(files) {
      console.log(`get files url, files count: ${files.length}`);
      console.log(files[0]);
      return files[0].presignData.url;
    }
  },

  mounted: function () {

    this.$emit('canAddAssets', this.canAddAssets);

    // Setting up dropzone & dropzone events
    this.dropzone = new Dropzone(document.querySelector(this.dropArea), {
      url: this.getFileUrl,
      // Upload one file at a time since we're using the S3 pre-signed URL scenario
      parallelUploads: 1,
      method: 'put',
      previewsContainer: this.$refs.emptyPreviewsContainer,
      clickable: this.$refs.emptyDropzoneContainer,
      previewTemplate: '<div></div>',
      thumbnailWidth: 100,
      thumbnailHeight: null,
      maxThumbnailFilesize: 20,
      autoProcessQueue: false,
      uploadMultiple: false,
      timeout: null,
      acceptedFiles: this.acceptedFiles,
      maxFilesize: this.maxFileSize,
      didFinishAddingFiles: false
    });

    this.dropzone.on('addedfiles', () => {
      this.$nextTick(() => {
        if (this.dropzone.getQueuedFiles().length > 0) {
          this.$refs.uploadForm.openModal()
        }
      })
    });

    this.dropzone.on('addedfile', file => {
      this.collapsed = false;

      if (this.isDuplicateFile(file)) {
        this.dropzone.removeFile(file);
        return;
      }

      if (!this.canAddAssets) {
        this.dropzone.removeFile(file);
        this.maxFilesToastVisible = true;
        return
      }

      const isValidFile = Dropzone.isValidFile(file, this.dropzone.options.acceptedFiles);
      if (!isValidFile || this.filesInclude(file)) {
        this.dropzone.removeFile(file);
        this.showErrorToast(`This file has an invalid format: ${file.name}`);
        return;
      }

      this.files.push(new FileObject(file));
    });

    this.dropzone.on('sending', (file, xhr, formData) => {
      // Hijack the xhr.send since Dropzone always upload file by using formData
      // ref: https://github.com/danialfarid/ng-file-upload/issues/743
      let _send = xhr.send
      xhr.send = () => {
        _send.call(xhr, file)
      }

      this.queueCompleted = false;

      let fileObject = this.findFileObject(file);
      if (fileObject) {
        fileObject.status = FileObject.UPLOADING;
      }

      // // append formAssetParams from the upload form
      // Object.keys(this.formAssetParams).forEach(key => {
      //   const val = this.formAssetParams[key];
      //   if (Array.isArray(val)) {
      //     val.forEach(v => {
      //       formData.append(`asset[${key}][]`, v)
      //     });
      //   } else {
      //     formData.append(`asset[${key}]`, val)
      //   }
      // });

      xhr.ontimeout = (e => {
        if (fileObject) {
          fileObject.error = 'timeout';
          fileObject.status = FileObject.ERROR;
        }
      });
    });

    this.dropzone.on('thumbnail', (file, dataUrl) => {
      let fileObject = this.findFileObject(file);
      if (fileObject && !fileObject.asset_url) {
        fileObject.asset_url = dataUrl
      }
    });

    this.dropzone.on('removedfile', (file) => {
      let fileObject = this.findFileObject(file);
      if (fileObject) {
        this.files.splice(this.files.indexOf(fileObject), 1);
      }
    });

    this.dropzone.on('success', (file, xhr) => {
      // Success gets called when S3 upload finishes
      // calling processQueue once only does dropzone.parallelUploads.count of files so we call it subsequently here
      // https://github.com/enyo/dropzone/issues/462
      this.dropzone.processQueue();

      let fileObject = this.findFileObject(file);
      if (fileObject.presignData.id) {
        this.$http.post(`${this.url}/${fileObject.presignData.id}/complete_upload`).then(fulfilled => {
          // Upload finished and we got a server object id
          const asset = normalize(fulfilled.body).get(Attributes.assetAttributes.concat(Attributes.prefixed('release_forms', Attributes.releaseFormAttributes)));
          fileObject.id = asset.id;
          fileObject.status = FileObject.SUCCESS;
          this.$emit('addedAsset', asset)
        }, rejected => {
          console.log("ERROR on COMPLETE_UPLOAD");
          console.log(rejected);
          fileObject.status = FileObject.ERROR
        })

      } else {
        console.log("error: success without presign data id");
        console.log(fileObject);
        fileObject.status = FileObject.ERROR
      }
    });

    this.dropzone.on('queuecomplete', () => {
      this.queueCompleted = true;
      if (this.filesWithErrors.length === 0) {
        this.reset();
        this.collapsed = true;
      }
    });

    this.dropzone.on('uploadprogress', (file, progress, bytesSent) => {
      let fileObject = this.findFileObject(file);
      if (fileObject) {
        fileObject.progress = progress;
      }
    });

    this.dropzone.on('error', (file, errorMessage, xhr) => {
      console.log('calling process queue on error');
      console.log(file);

      if (xhr) {
        this.dropzone.processQueue();
      }

      let fileObject = this.findFileObject(file);
      if (fileObject) {
        fileObject.error = errorMessage;
        fileObject.status = FileObject.ERROR;
        fileObject.canRetry = (xhr !== null && xhr !== undefined);
      }
    });

    this.preventSaveLoss(() => {
      return this.files.length > 0 && this.queueCompleted === false
    })
  },

  components: {UploadItem, UploadForm}
}
</script>